import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import { useQuery } from '@tanstack/react-query'
import { GetAPIparams, handlePrint } from '@utils/assessmentReportUtils'
import { Flex, Spin } from 'antd'
import { useState } from 'react'
import { z } from 'zod'

import { useLocation, useNavigate } from '@suntex.core/nav'
import { PlayerId } from '@suntex.fim/api-client'
import { gradeToPhase } from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import {
  $GoalsIndexPhase,
  $GymMode,
  $KnowAndShowLevel,
  $SpecialGradeLevel,
  EntityLevel,
  GoalsIndexPhase,
  GymMode,
  KnowAndShowLevel,
  SpecialGradeLevel
} from '@suntex.fim/enums'
import {
  useFramework,
  usePersistedState,
  useRotateDevice
} from '@suntex.fim/framework'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { Button } from '@suntex.fim/ui'

import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import GoalsIndex from './GoalIndexCharts/GoalIndex'
import Gym from './GoalIndexCharts/Gym'
import KnowAndShow from './GoalIndexCharts/KnowAndShow'
import SkillSet from './GoalIndexCharts/SkillSet'

/**
 * returns JSX for report summary
 */
function AssessmentReportGoalIndex() {
  const { api } = useFramework()
  const location = useLocation()
  const {
    initialPhase,
    goalsIndexPhase,
    gymLevel: queryGymLevel,
    skillSetLevel: querySkillSetLevel,
    kasLevel: queryKasLevel
  } = useAppQueryString()
  const [gymLevel, setGymLevel] = useState<GymMode | null>(
    $GymMode.fromOptional(queryGymLevel) ?? null
  )
  const [skillSetLevel, setSkillSetLevel] = useState<GoalsIndexPhase | null>(
    $GoalsIndexPhase.fromOptional(querySkillSetLevel ?? undefined) ?? null
  )
  const [kasLevel, setKasLevel] = useState<KnowAndShowLevel | null>(
    $KnowAndShowLevel.fromOptional(queryKasLevel ?? undefined) ?? null
  )
  const team = useOptionalTeamInfo()
  const { jwt, login } = useAuth(isAuthenticated)
  const entityLevel = login.entityLevel
  const fileName = `${team?.name || login.schoolName}_goals-index`
  const isPlayerRole =
    entityLevel === EntityLevel.Player || location.pathname.includes('player')
  const [isExporting, setIsExporting] = useState(false)
  const [dropdownFilters, setDropdownFilters] = useAssessmentDropdownFilters()
  const isSchool =
    entityLevel === EntityLevel.School && location.pathname.includes('school')

  useRotateDevice()
  const navigate = useNavigate()

  const { id, year, grade } = GetAPIparams(
    isSchool || location.pathname.includes('school'),
    dropdownFilters,
    isPlayerRole && location.pathname.includes('player')
  )

  // For DDL defaults, use the player's personal grade for players.
  // For API calls, use AllGrades at team level so data isn't filtered by grade.
  const defaultGrade = isPlayerRole
    ? $SpecialGradeLevel.from(login.gradeId)
    : grade
  const apiGrade =
    isPlayerRole && !location.pathname.includes('player')
      ? SpecialGradeLevel.AllGrades
      : grade

  const isPlayerPath = location.pathname.includes('player')
  const { data: playerData, isLoading } = useQuery({
    queryKey: ['assessment', 'playerSummary', id],
    queryFn: () =>
      api.get_Assessment_PlayerSummary({
        playerId: id as PlayerId
      }),
    enabled: id != null && isPlayerPath
  })

  // Phase from query params (deep links / print views)
  const queryPhase = initialPhase
    ? $GoalsIndexPhase.fromSlug(initialPhase.toLowerCase())
    : goalsIndexPhase &&
        Number(goalsIndexPhase) > 0 &&
        Number(goalsIndexPhase) < 5
      ? $GoalsIndexPhase.from(goalsIndexPhase)
      : null

  // Persisted user preference (localStorage, per-user)
  const [persistedPhase, setPhase] = usePersistedState(
    'goalsIndexPhase',
    z
      .union([
        z.literal(GoalsIndexPhase.Early),
        z.literal(GoalsIndexPhase.Elementary),
        z.literal(GoalsIndexPhase.Intermediate),
        z.literal(GoalsIndexPhase.Advanced)
      ])
      .nullable()
      .catch(null)
  )

  // Grade-based default (re-computes when async team/player data loads)
  const gradeBasedPhase = (() => {
    const g = isPlayerRole ? login.gradeId : (team?.grade ?? playerData?.grade)
    return g != null
      ? gradeToPhase($SpecialGradeLevel.from(g))
      : GoalsIndexPhase.Elementary
  })()

  // Priority: query param > saved preference > grade-based default
  const phase = queryPhase ?? persistedPhase ?? gradeBasedPhase

  return (
    <Flex vertical gap="20px" style={{ position: 'relative' }}>
      {/* Loader overlay when loading and popup not open */}
      {(isLoading || isExporting) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Spin size="large" tip="Loading..." />
        </div>
      )}
      <AssessmentReportNavigation
        exportButton={
          !isPlayerRole && (
            <Flex gap={10} className="export-btn print-hidden">
              <Button
                shape="round"
                intent="secondary"
                onClick={() => {
                  if (isSchool) {
                    navigate(
                      `/school/${id}/assessment-report/goals-by-team?phase=${phase}`
                    )
                  } else {
                    navigate(
                      `/team/${id}/assessment-report/goals-by-player?phase=${phase}`
                    )
                  }
                }}
              >
                View Report by {isSchool ? 'Team' : 'Player'}
              </Button>
              <Button
                shape="round"
                intent="secondary"
                onClick={() => {
                  void handlePrint({
                    setIsLoading: setIsExporting,
                    fileName
                  })
                }}
              >
                View Printer Friendly Version
              </Button>
            </Flex>
          )
        }
        dropdownFilters={dropdownFilters}
        setDropDownFilters={setDropdownFilters}
        isSchool={isSchool || location.pathname.includes('school')}
        isPlayer={isPlayerRole && location.pathname.includes('player')}
        displayReport={
          entityLevel !== EntityLevel.Player &&
          !location.pathname.includes('player')
        }
        title={entityLevel === EntityLevel.Player ? 'Goals Index' : undefined}
      />
      {playerData && (
        <AssessmentReportSummaryDashboard
          dropdownFilters={dropdownFilters}
          isSchool={isSchool || location.pathname.includes('school')}
          isPlayer={isPlayerRole && location.pathname.includes('player')}
          player={playerData}
        />
      )}
      {/* result */}
      <div className="main-result">
        {/* Updated toolbar design */}
        <Flex vertical className="tiles-container">
          <GoalsIndex
            phase={phase}
            setPhase={setPhase}
            id={id}
            year={year}
            grade={apiGrade}
          />
          <Gym
            phase={phase}
            id={id}
            year={year}
            grade={apiGrade}
            defaultGrade={defaultGrade}
            entityLevel={entityLevel}
            gymLevel={gymLevel}
            onLevelChange={(level) => {
              setGymLevel(level)
            }}
          />
          <SkillSet
            id={id}
            year={year}
            grade={apiGrade}
            defaultGrade={defaultGrade}
            entityLevel={entityLevel}
            skillSetLevel={skillSetLevel}
            onLevelChange={(level) => {
              setSkillSetLevel(level)
            }}
          />
          <KnowAndShow
            id={id}
            year={year}
            grade={apiGrade}
            defaultGrade={defaultGrade}
            entityLevel={entityLevel}
            kasLevel={kasLevel}
            onLevelChange={(level) => {
              setKasLevel(level)
            }}
          />
        </Flex>
      </div>
    </Flex>
  )
}

export default AssessmentReportGoalIndex
